import { useAuthStore } from "../store/authStore";
import { usePromptStore } from "../store/promptStore";
import { Screen } from "../types/enums";
import websocketClient from "../websocket";
import {
  PromptRequest,
  PromptTypeKey,
  PromptCheckboxType,
  PromptSelectType,
  PromptNumberCodeType,
} from "../types/promptTypes";
interface FetchOptions extends RequestInit {
  errorCode?: string;
  displayMessage?: string;
  responseType?: "json" | "text" | "none";
}

/**
 * A wrapper around fetch that handles error states consistently
 * @param url The URL to fetch
 * @param options Standard fetch options plus custom error handling options
 * @returns The response data if successful, { success: true } for empty responses, or null on error
 */
export const fetchWithErrorHandling = async <T>(
  url: string,
  options: FetchOptions = {}
): Promise<T | { success: true } | null> => {
  const {
    errorCode = "api_error",
    displayMessage = "An error occurred during the API request",
    responseType = "json",
    ...fetchOptions
  } = options;

  try {
    const response = await fetch(url, fetchOptions);

    // Log the full response for debugging
    console.debug("Full response:", {
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries([...response.headers.entries()]),
      url: response.url,
      type: response.type,
      ok: response.ok,
    });

    if (!response.ok) {
      let errorMessage = response.statusText;

      try {
        // Only try to parse JSON if the content exists and is JSON
        const contentType = response.headers.get("content-type");
        if (
          contentType &&
          contentType.includes("application/json") &&
          response.status !== 204
        ) {
          const text = await response.text();
          if (text) {
            const errorData = JSON.parse(text);
            console.debug(`Parsed error data:`, errorData);

            // Check for error message in both message and error fields
            if (errorData.error) {
              errorMessage = errorData.error;
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } else {
          errorMessage = response.statusText;
        }
      } catch (parseError) {
        // If JSON parsing fails, use the status text
        console.error(
          `API request failed: ${response.status} ${response.statusText}`
        );
      }

      usePromptStore.getState().setError({
        code: errorCode,
        display_message: displayMessage,
        message: errorMessage,
      });
      useAuthStore.getState().setScreen(Screen.ERROR);

      throw new Error(errorMessage);
    }

    // standard RESTful pattern: (oauthExternalHandler and promptsExternalHandler) just return HTTP 200 OK with empty body
    if (response.status === 204 || responseType === "none") {
      return { success: true } as unknown as T;
    }

    // For text responses, return the text directly
    if (responseType === "text") {
      return (await response.text()) as unknown as T;
    }

    // For JSON responses, need to read the text first
    const text = await response.text();

    // Empty response with 200 status means success with no content
    if (!text) {
      return { success: true } as unknown as T;
    }

    // Parse non-empty JSON response
    return JSON.parse(text) as T;
  } catch (error) {
    console.error(
      `API request failed: ${
        error instanceof Error ? error.message : String(error)
      }`
    );

    usePromptStore.getState().setError({
      code: errorCode,
      display_message: displayMessage,
      message: error instanceof Error ? error.message : String(error),
    });
    websocketClient.disconnect();
    useAuthStore.getState().setScreen(Screen.ERROR);

    throw error;
  }
};

export function isPromptValueValid(prompt: PromptRequest, value: any): boolean {
  switch (prompt.type) {
    case PromptTypeKey.CHECKBOX: {
      let selected: any[];
      try {
        selected = value || [];
      } catch {
        return false;
      }

      const { multiple, min, max } = prompt.attributes as PromptCheckboxType;

      if (multiple) {
        if (min !== undefined && selected.length < min) return false;
        if (max !== undefined && selected.length > max) return false;
        return selected.length > 0;
      } else {
        return selected.length === 1;
      }
    }

    case PromptTypeKey.NUMBER_CODE: {
      const numberCodeAttrs = prompt.attributes as PromptNumberCodeType;
      // For NUMBER_CODE, if it has a length attribute, ensure every digit is filled
      if (numberCodeAttrs.length) {
        const expectedLength = numberCodeAttrs.length;
        return value.length === expectedLength && /^\d+$/.test(value);
      }
      return value.trim() !== "";
    }

    case PromptTypeKey.TEXT:
    case PromptTypeKey.SELECT:
    case PromptTypeKey.CODE:
    case PromptTypeKey.PASSWORD:
      return value.trim() !== "";

    case PromptTypeKey.LOGIN:
      // For login prompts, we need a valid provider object
      // Empty string (initial value) should be invalid
      if (value === "" || value === null || value === undefined) return false;
      return typeof value === "object" && value.id;

    default:
      return true;
  }
}

export function initializePromptValues(prompts: PromptRequest[]): string[] {
  return prompts.map((prompt) => {
    const attrs = prompt.attributes;

    switch (prompt.type) {
      case PromptTypeKey.CHECKBOX: {
        const { selected } = attrs as PromptCheckboxType;
        return selected ? JSON.stringify(selected) : "[]";
      }

      case PromptTypeKey.SELECT: {
        const { selected } = attrs as PromptSelectType;
        return selected ? JSON.stringify([selected]) : "[]"; // SELECT is single value, but stored as stringified array for consistency
      }

      case PromptTypeKey.TEXT:
      case PromptTypeKey.PASSWORD:
      case PromptTypeKey.CODE:
      case PromptTypeKey.NUMBER_CODE: {
        // Generic catch for all text-like inputs
        if ("value" in attrs && attrs.value) {
          return String(attrs.value);
        }
        return "";
      }

      case PromptTypeKey.LOGIN:
        // For login prompts, initialize with null or empty object
        return "";

      default:
        return "";
    }
  });
}

export const isProduction = (): boolean => {
  // ① Vercel builds & runtime  → import.meta.env.VERCEL_ENV
  // ② Local vite dev           → fallback to import.meta.env.MODE
  return (import.meta.env.VERCEL_ENV ?? import.meta.env.MODE) === "production";
};
